import { fetchAuthSession } from 'aws-amplify/auth'; // Import fetchAuthSession
import { Download } from 'lucide-react'; // Using Download icon for download button
import React, { useEffect, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { clampDateToToday, getTodayISODate, getYesterdayISODate } from '../../utils/dateHelpers';
import { SearchBar } from '../ui';

const SiteReports = () => {
  // Log screen view
  useScreenLogging('SiteReports', {
    component: 'SiteReports',
  });
    // State for site selection dropdown
    const [selectedSite, setSelectedSite] = useState('');
    const [siteOptions, setSiteOptions] = useState([]);
    const [loadingSites, setLoadingSites] = useState(false);
    
    // State for search functionality
    const [searchTerm, setSearchTerm] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showAllSitesDropdown, setShowAllSitesDropdown] = useState(false);

    // State for date picker
    const [selectedDate, setSelectedDate] = useState(() => getYesterdayISODate()); // Stores YYYY-MM-DD string

    // State for main report data (now representing shifts/time slots)
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [error, setError] = useState(null);

    const BASE_URL = 'https://api.innoworx.ai/Stage'; // From Reports API Documentation

    // Cache configuration
    const CACHE_CONFIG = {
        SITES_TTL: 60 * 60 * 1000, // 1 hour for site options
        REPORTS_TTL: 30 * 60 * 1000, // 30 minutes for site reports
        CACHE_PREFIX: 'siteReports_cache_',
    };

    // Cache utility functions
    const getCacheKey = (type, ...params) => {
        return `${CACHE_CONFIG.CACHE_PREFIX}${type}_${params.join('_')}`;
    };

    const getCachedData = (cacheKey) => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (!cached) return null;

            const { data, timestamp, ttl } = JSON.parse(cached);
            const now = Date.now();
            
            // Check if cache is expired
            if (now - timestamp > ttl) {
                localStorage.removeItem(cacheKey);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error reading from cache:', error);
            localStorage.removeItem(cacheKey);
            return null;
        }
    };

    const setCachedData = (cacheKey, data, ttl) => {
        try {
            const cacheEntry = {
                data,
                timestamp: Date.now(),
                ttl,
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } catch (error) {
            console.error('Error writing to cache:', error);
            // If storage is full, try to clear old cache entries
            clearExpiredCache();
        }
    };

    const clearExpiredCache = () => {
        try {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith(CACHE_CONFIG.CACHE_PREFIX)) {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        try {
                            const { timestamp, ttl } = JSON.parse(cached);
                            if (Date.now() - timestamp > ttl) {
                                localStorage.removeItem(key);
                            }
                        } catch (e) {
                            // Invalid cache entry, remove it
                            localStorage.removeItem(key);
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Error clearing expired cache:', error);
        }
    };

    // Function to get the authentication token
    const getAuthToken = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            if (tokens && tokens.idToken) {
                return tokens.idToken.toString();
            } else {
                console.error('No ID Token found in the current session.');
                return null;
            }
        } catch (error) {
            console.error('Error fetching authentication session:', error);
            return null;
        }
    };

    // Cleanup expired cache entries on mount
    useEffect(() => {
        clearExpiredCache();
    }, []);

    // Effect to fetch site options for the dropdown
    useEffect(() => {
        const fetchSiteOptions = async () => {
            setLoadingSites(true);
            setError(null);
            
            // Check cache first
            const cacheKey = getCacheKey('sites');
            const cachedSites = getCachedData(cacheKey);
            
            if (cachedSites) {
                // Ensure cached sites are sorted alphabetically
                const sortedCachedSites = [...cachedSites].sort((a, b) => {
                    const nameA = (a?.site_name || a?.name || '').toLowerCase();
                    const nameB = (b?.site_name || b?.name || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });
                setSiteOptions(sortedCachedSites);
                setLoadingSites(false);
                return;
            }

            try {
                const token = await getAuthToken();
                if (!token) {
                    setError('Authentication token is missing. Please ensure a user is logged in.');
                    setLoadingSites(false);
                    return;
                }

                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };

                // Endpoint: GET /site/getall
                const response = await fetch(`${BASE_URL}/site/getall`, {
                    method: 'GET',
                    headers: headers,
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                }

                const data = await response.json();
                // Documentation Response: [{ "site_id": "...", "site_name": "...", "address": "..." }]
                if (Array.isArray(data)) {
                    // Sort sites alphabetically by site name
                    const sortedSites = [...data].sort((a, b) => {
                        const nameA = (a?.site_name || a?.name || '').toLowerCase();
                        const nameB = (b?.site_name || b?.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                    setSiteOptions(sortedSites);
                    // Cache the sorted site options
                    setCachedData(cacheKey, sortedSites, CACHE_CONFIG.SITES_TTL);
                    
                    // Log API event for fetching site options
                    loggingService.logInfo(
                        'api_success_site_reports',
                        `Successfully fetched site options`,
                        {
                            endpoint: '/site/getall',
                            method: 'GET',
                            sitesCount: sortedSites.length,
                        }
                    ).catch(logError => {
                        console.warn('[SiteReports] Failed to log event:', logError);
                    });
                    
                    // Don't set default site here - let user select after choosing date
                } else {
                    console.warn("API response for site options is not an array:", data);
                    setError("Received unexpected data format for site options.");
                    setSiteOptions([]);
                }

            } catch (err) {
                console.error("Error fetching site options:", err);
                setError(`Failed to load site options: ${err.message}`);
                setSiteOptions([]);
            } finally {
                setLoadingSites(false);
            }
        };

        if (BASE_URL !== 'YOUR_API_BASE_URL') {
            fetchSiteOptions();
        } else {
            setError("API Base URL is not configured. Please update BASE_URL.");
            setLoadingSites(false);
            setSiteOptions([]);
        }
    }, [BASE_URL]); // Re-run if BASE_URL changes

    // Effect to fetch site reports based on selected site and date
    useEffect(() => {
        const fetchSiteReports = async () => {
            if (!selectedSite || !selectedDate) {
                setReports([]);
                return; // Don't fetch if site or date is not selected
            }

            setLoadingReports(true);
            setError(null);

            // Check cache first
            const cacheKey = getCacheKey('reports', selectedSite, selectedDate);
            const cachedReports = getCachedData(cacheKey);
            
            if (cachedReports !== null) {
                // null means no cache, but empty array is valid cached data (no reports found)
                setReports(cachedReports);
                setLoadingReports(false);
                setError(null);
                return;
            }

            try {
                const token = await getAuthToken();
                if (!token) {
                    setError('Authentication token is missing. Please ensure a user is logged in.');
                    setLoadingReports(false);
                    return;
                }

                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };

                // API endpoint for site reports: GET /report/sites, Query Parameters: site_id, date
                const formattedDateForApi = selectedDate; // selectedDate is already YYYY-MM-DD string
                const apiUrl = `${BASE_URL}/report/sites?site_id=${selectedSite}&date=${formattedDateForApi}`; // Changed siteId to site_id

                const response = await fetch(apiUrl, {
                    method: 'GET',
                    headers: headers,
                });

                // Check for 404 specifically to display "No reports found" message
                if (response.status === 404) {
                    const emptyReports = []; // No reports found, so set an empty array
                    setReports(emptyReports);
                    // Cache the empty result as well
                    setCachedData(cacheKey, emptyReports, CACHE_CONFIG.REPORTS_TTL);
                    setLoadingReports(false);
                    setError(null); // Clear any previous errors
                    return; // Exit function
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                }

                const data = await response.json();
                // Documentation Response: [{ "file_name": "...", "url": "..." }]
                // The documentation shows an array of objects, each with file_name and url.
                // The current component expects a 'time' property, which is not in the doc.
                // Assuming 'file_name' can be used as the display text for the report entry.
                if (Array.isArray(data)) {
                    setReports(data);
                    // Cache the reports
                    setCachedData(cacheKey, data, CACHE_CONFIG.REPORTS_TTL);
                    
                    // Log API event for viewing site report
                    loggingService.logInfo(
                        'report_view_site',
                        `User viewed site report for site ${selectedSite} on date ${formattedDateForApi}`,
                        {
                            endpoint: '/report/sites',
                            method: 'GET',
                            siteId: selectedSite,
                            date: formattedDateForApi,
                            reportCount: data.length,
                        }
                    ).catch(logError => {
                        console.warn('[SiteReports] Failed to log event:', logError);
                    });
                } else {
                    console.warn("API response for site reports is not an array:", data);
                    setError("Received unexpected data format for site reports.");
                    setReports([]);
                }

            } catch (err) {
                console.error("Error fetching site reports:", err);
                setError(`Failed to load site reports: ${err.message}`);
                setReports([]);
            } finally {
                setLoadingReports(false);
            }
        };

        const timer = setTimeout(() => {
            if (BASE_URL !== 'YOUR_API_BASE_URL') {
                fetchSiteReports();
            } else {
                setError("API Base URL is not configured. Please update BASE_URL.");
                setLoadingReports(false);
                setReports([]);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [selectedSite, selectedDate, BASE_URL]);

    // Handle search functionality
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        if (value.length > 0) {
            // Filter through real site data
            const filteredSites = siteOptions.filter(site => 
                site.site_name && site.site_name.toLowerCase().includes(value.toLowerCase())
            );
            setSearchSuggestions(filteredSites.map(site => site.site_name));
        } else {
            setSearchSuggestions([]);
        }
    };

    // Handle search bar focus - show all sites
    const handleSearchBarClick = () => {
        if (siteOptions.length > 0) {
            // Show all sites when clicking on search bar
            const allSiteNames = siteOptions.map(site => site.site_name);
            setSearchSuggestions(allSiteNames);
            setShowAllSitesDropdown(true);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const searchContainer = document.querySelector('.search-container');
            if (searchContainer && !searchContainer.contains(event.target)) {
                setShowAllSitesDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchSubmit = (value) => {
        // Find the selected site from siteOptions
        const site = siteOptions.find(s => s.site_name === value);
        if (site) {
            setSelectedSite(site.site_id);
            setSearchTerm(site.site_name);
        }
        setShowAllSitesDropdown(false);
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        setSearchSuggestions([]);
        setShowAllSitesDropdown(false);
        // Find and set the selected site with full information
        const site = siteOptions.find(s => s.site_name === suggestion);
        if (site) {
            setSelectedSite(site.site_id);
        }
    };

    // handleDownload now uses the 'url' from the API response
    const handleDownload = (reportUrl) => {
        if (!reportUrl) {
            setError('No download URL available for this report.');
            return;
        }

        // Open the URL in a new tab to initiate download
        window.open(reportUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="flex flex-col flex-1">
            {/* Header Title */}
            <div className="self-start mb-6">
                <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">Site Reports</h1>
            </div>

            <div className="mb-2 text-left">
                <h5 className={typography.h5} style={{color: '#000000'}}>
                Please search and select the site to view site reports
                </h5>
            </div>
            {/* Controls Bar: Site Search Dropdown and Date Picker */}
            <div className="flex flex-col md:flex-row  gap-4 mb-6">
                <div className="relative w-full md:w-90 search-container">
                    {loadingSites ? (
                        <div className="block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white backdrop-blur-[30px] text-neutral-400 text-lg font-bold font-['Figtree'] leading-snug cursor-not-allowed flex items-center">
                            Loading Sites...
                        </div>
                    ) : (
                        <>
                            <div onClick={handleSearchBarClick}>
                                <SearchBar
                                    placeholder={siteOptions.length === 0 ? "No sites available" : "Search by site name"}
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    onSearch={handleSearchSubmit}
                                    suggestions={searchSuggestions}
                                    onSuggestionClick={handleSuggestionClick}
                                    className="w-full cursor-pointer"
                                    disabled={siteOptions.length === 0}
                                />
                            </div>
                            {/* Custom dropdown for all sites */}
                            {showAllSitesDropdown && siteOptions.length > 0 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                                    <div className="p-2 border-b border-gray-200 bg-gray-50">
                                        <p className="text-sm font-medium text-gray-700">
                                            All Sites ({siteOptions.length})
                                        </p>
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {searchSuggestions.length > 0 ? (
                                            searchSuggestions.map((siteName, index) => {
                                                const site = siteOptions.find(s => s.site_name === siteName);
                                                return (
                                                    <div
                                                        key={site?.site_id || index}
                                                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                                        onClick={() => handleSuggestionClick(siteName)}
                                                    >
                                                        <div className="font-medium text-gray-900">{siteName}</div>
                                                        {site?.address && (
                                                            <div className="text-sm text-gray-500 truncate">
                                                                {site.address}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : searchTerm.length > 0 ? (
                                            <div className="p-4 text-center text-gray-500">
                                                No matching sites found
                                            </div>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">
                                                Start typing to search sites
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Date Picker */}
                <div className="relative w-full md:w-80">
                    <input
                        type="date"
                        className={`block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug
                            ${selectedDate ? 'outline-green-700 text-green-700' : 'outline-white text-neutral-900'}`
                        }
                        value={selectedDate}
                        max={getTodayISODate()}
                        onChange={(e) => {
                            const nextValue = clampDateToToday(e.target.value) || getYesterdayISODate();
                            setSelectedDate(nextValue);
                            setReports([]); // Clear reports when date changes (will be refetched automatically)
                            setShowAllSitesDropdown(false); // Close dropdown when date changes
                        }}
                    />
                    {/* <CalendarDays className={`absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 ${selectedDate ? 'text-green-700' : 'text-neutral-400'} pointer-events-none`} /> */}
                </div>
                
            </div>

            

            {/* Site Reports Content */}
            <div className="flex-1 inline-flex justify-start items-start gap-4 flex-wrap content-start overflow-y-auto pr-4">
                {loadingReports ? (
                    <div className="flex items-center justify-center w-full h-full text-gray-500">Loading Site Reports...</div>
                ) : error ? (
                    <div className="flex items-center justify-center w-full h-full text-red-500">{error}</div>
                ) : reports.length > 0 ? (
                    reports.map((report, index) => { // Added index for unique key if report.url is not guaranteed unique
                        // Find the selected site's name
                        const selectedSiteData = siteOptions.find(site => site.site_id === selectedSite);
                        const siteName = selectedSiteData?.site_name || 'Unknown Site';
                        
                        return (
                            <div
                                key={report.url || index}
                                className="self-stretch inline-flex justify-between items-center w-full p-4 bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-white backdrop-blur-[20px] cursor-pointer transition-shadow hover:shadow-lg"
                                role="button"
                                tabIndex={0}
                                onClick={() => handleDownload(report.url)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleDownload(report.url);
                                    }
                                }}
                            >
                                <div className="flex flex-row justify-start items-start gap-2">
                                    {/* Display site name */}
                                    <div className="justify-center text-black text-lg font-semibold font-['Open_Sans'] leading-6 text-gray-600">
                                        {siteName}
                                    </div>
                                    {/* Display file_name as per documentation */}
                                    <div className="justify-center text-black text-lg font-semibold font-['Open_Sans'] leading-6">
                                        {report.file_name}
                                    </div>
                                </div>
                                <button
                                    className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                                    onClick={event => {
                                        event.stopPropagation();
                                        handleDownload(report.url);
                                    }} // Pass report.url for download
                                >
                                    <Download className="w-6 h-6 text-white stroke-white" strokeWidth={2} />
                                </button>
                            </div>
                        );
                    })
                ) : (
                    <div className="flex items-center justify-center w-full h-full text-gray-500">
                        {!selectedSite ? "Please select a site and date." :
                        !selectedDate ? "Please select a date." :  "No reports found for the selected site and date."}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SiteReports;
